import type { QuestionOption } from '@common/types/templateFormFields';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box, IconButton, TextField, Tooltip, useTheme } from '@mui/material';
import type { ChangeEvent, DragEvent } from 'react';
import { useRef } from 'react';
import type { OptionEditorFieldData, OptionErrorType } from './types';

interface ChoiceFieldProps {
    index: number;
    fieldData?: OptionEditorFieldData;
    option: QuestionOption;
    error?: OptionErrorType;
    onChange: (value: string) => void;
    onRemove: () => void;
    onDragStart: (index: number) => void;
    onDrop: (index: number) => void;
    isSuperAdmin?: boolean;
}

export default function ChoiceField({
    index,
    fieldData,
    option,
    error,
    onChange,
    onRemove,
    onDragStart,
    onDrop,
    isSuperAdmin,
}: ChoiceFieldProps) {
    const draggerRef = useRef<HTMLElement>(undefined);
    const optionRef = useRef<HTMLElement>(undefined);
    const theme = useTheme();
    const hasMatch = fieldData?.questionSchema?.conditions?.some(
        (cond) => cond.value === option.optionLabel,
    );
    return (
        <Box
            component={'div'}
            draggable={true}
            ref={optionRef}
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                background: theme.palette.neutral.white,
                padding: '4px 0',
            }}
            onDragLeave={(e) => {
                if (e.currentTarget.classList.contains('dropReady')) {
                    e.currentTarget.classList.remove('dropReady');
                }
            }}
            onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add('dropReady');
            }}
            onDragStart={() => {
                onDragStart(index - 1);
            }}
            onDrop={(e: DragEvent<HTMLDivElement>) => {
                e.preventDefault();
                e.stopPropagation();
                if (e.currentTarget.classList.contains('dropReady')) {
                    e.currentTarget.classList.remove('dropReady');
                }
                onDrop(index - 1);
            }}
        >
            {/* LEFT: drag + text field */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    flex: 1,
                }}
            >
                <Box
                    ref={draggerRef}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'grab',
                    }}
                    onMouseDown={() => {
                        if (draggerRef.current) {
                            draggerRef.current.style.cursor = 'grabbing';
                        }
                    }}
                    onMouseUp={() => {
                        if (draggerRef.current) {
                            draggerRef.current.style.cursor = 'grab';
                        }
                    }}
                    onMouseLeave={() => {
                        if (draggerRef.current) {
                            draggerRef.current.style.cursor = 'grab';
                        }
                    }}
                >
                    <DragIndicatorIcon sx={{ fontSize: 16 }} />
                </Box>

                <TextField
                    sx={{ width: '100%' }}
                    value={option?.optionLabel}
                    label={`Option ${index}`}
                    disabled={
                        hasMatch
                            ? true
                            : isSuperAdmin
                              ? false
                              : !!(
                                    fieldData?.questionSchema?.restrictOptionEdit ||
                                    fieldData?.questionSchema?.conditions?.some(
                                        (condition) => condition?.value === option?.optionValue,
                                    ) ||
                                    fieldData?.isLocked
                                )
                    }
                    error={error?.state}
                    helperText={error?.message || undefined}
                    onChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        onChange(event.target.value)
                    }
                />
            </Box>

            {/* RIGHT: icons, always keep space for Info */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    marginLeft: '12px',
                }}
            >
                <IconButton
                    onClick={onRemove}
                    disabled={
                        hasMatch
                            ? true
                            : isSuperAdmin
                              ? false
                              : !!(
                                    fieldData?.questionSchema?.restrictOptionEdit ||
                                    fieldData?.questionSchema?.conditions?.some(
                                        (condition) => condition?.value === option?.optionValue,
                                    ) ||
                                    fieldData?.isLocked
                                )
                    }
                >
                    <RemoveCircleOutlineIcon />
                </IconButton>

                {/* Info icon slot, always reserve space */}
                <Box sx={{ width: '40px', display: 'flex', justifyContent: 'center' }}>
                    {!!fieldData?.questionSchema?.conditions?.length &&
                        hasMatch &&
                        !fieldData?.questionSchema?.restrictOptionEdit &&
                        !fieldData?.questionValidationSchema?.required && (
                            <Tooltip title="To edit or remove this option, please first delete the related child field(s).">
                                <IconButton>
                                    <InfoOutlinedIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                </Box>
            </Box>
        </Box>
    );
}
